import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { Medal, RefreshCw, Trophy } from 'lucide-react';
import Avatar from '../../components/avatar/avatar.component';
import {
  fetchTopDonatorApi,
  fetchTopFollowerApi,
  fetchTopInteractionsApi,
  fetchTopReceiversApi,
} from '../../api/user-profile/leaderboard';
import { formatPeriodLeft, HonorPeriod, periodRange } from '../../utils/honor-period';
import {
  formatHonorValue,
  HonorKind,
  HonorPeriodKey,
  HonorRow,
  HonorTabKey,
  maskEmailLike,
  parseHonorQuery,
  toHonorRows,
} from '../../utils/honor-rows';

const TABS: { key: HonorTabKey; label: string }[] = [
  { key: 'tuong-tac', label: 'Được tương tác nhiều nhất' },
  { key: 'donate', label: 'Donate nhiều nhất' },
  { key: 'nhan-donate', label: 'Nhận donate nhiều nhất' },
  { key: 'theo-doi', label: 'Được theo dõi nhiều nhất' },
];

const PERIODS: { key: HonorPeriodKey; label: string }[] = [
  { key: 'tuan', label: 'Tuần này' },
  { key: 'thang', label: 'Tháng này' },
  { key: 'tat-ca', label: 'Mọi thời gian' },
];

const PAGE_SIZE = 20;

/** Gọi đúng nguồn API tương ứng với `kind`, đã chuẩn hoá về HonorRow[]. */
const fetchHonorRows = async (kind: HonorKind, period: HonorPeriod): Promise<HonorRow[]> => {
  const { startTime, endTime } = periodRange(period, new Date());

  let response: unknown;
  switch (kind) {
    case 'followers':
      response = await fetchTopFollowerApi({ startTime, endTime, pageSize: PAGE_SIZE, pageNumber: 1 });
      break;
    case 'donator':
      response = await fetchTopDonatorApi({ startTime, endTime, pageSize: PAGE_SIZE, pageNumber: 1 });
      break;
    case 'receives':
      response = await fetchTopReceiversApi({ startTime, endTime, pageSize: PAGE_SIZE, pageNumber: 1 });
      break;
    case 'interactions':
    default:
      response = await fetchTopInteractionsApi({ startTime, endTime, take: PAGE_SIZE });
      break;
  }

  return toHonorRows(kind, response);
};

/** Tên hiển thị + @username; bấm mở trang cá nhân khi có username. */
const HonorName = ({
  row,
  align = 'left',
}: {
  row: HonorRow;
  align?: 'left' | 'center';
}) => {
  const alignCls = align === 'center' ? 'text-center' : 'text-left';
  const content = (
    <div className={`min-w-0 ${alignCls}`}>
      <p className="truncate font-semibold ui-heading">{row.fullName}</p>
      {row.userName ? <p className="truncate text-xs ui-muted">@{maskEmailLike(row.userName)}</p> : null}
    </div>
  );

  if (!row.userName) return content;

  return (
    <Link to={`/user/${row.userName}`} className="min-w-0 hover:underline">
      {content}
    </Link>
  );
};

const RANK_STYLE: Record<1 | 2 | 3, { ring: string; medal: string; order: string; topGap: string }> = {
  1: { ring: 'ring-amber-400', medal: 'text-amber-500', order: 'order-2', topGap: 'pt-0' },
  2: { ring: 'ring-slate-300 dark:ring-slate-500', medal: 'text-slate-400', order: 'order-1', topGap: 'pt-6 sm:pt-8' },
  3: { ring: 'ring-orange-300', medal: 'text-orange-400', order: 'order-3', topGap: 'pt-8 sm:pt-10' },
};

const PodiumItem = ({ row, rank, kind }: { row?: HonorRow; rank: 1 | 2 | 3; kind: HonorKind }) => {
  if (!row) return <div className={`flex-1 ${RANK_STYLE[rank].order}`} />;

  const style = RANK_STYLE[rank];

  return (
    <div className={`flex flex-1 flex-col items-center gap-1.5 ${style.order} ${style.topGap}`}>
      <div className="relative">
        <div className={`rounded-full ring-4 ${style.ring}`}>
          <Avatar avatar={row.avatar || ''} label={row.fullName} size={rank === 1 ? 'xl' : 'lg'} />
        </div>
        <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow dark:bg-slate-800">
          <Medal size={14} className={style.medal} />
        </span>
      </div>
      <HonorName row={row} align="center" />
      <p className="text-sm font-bold text-primary">{formatHonorValue(kind, row.value)}</p>
      {row.detail ? <p className="text-center text-[11px] ui-muted">{row.detail}</p> : null}
    </div>
  );
};

const HonorPodium = ({ kind, rows }: { kind: HonorKind; rows: HonorRow[] }) => (
  <div className="ui-card mb-4 flex items-end gap-2 px-2 pb-4 pt-6 sm:gap-4 sm:px-6">
    <PodiumItem row={rows[1]} rank={2} kind={kind} />
    <PodiumItem row={rows[0]} rank={1} kind={kind} />
    <PodiumItem row={rows[2]} rank={3} kind={kind} />
  </div>
);

const HonorList = ({ kind, rows, startRank }: { kind: HonorKind; rows: HonorRow[]; startRank: number }) => (
  <div className="ui-card divide-y divide-[#E5E7EB] dark:divide-slate-700">
    {rows.map((row, idx) => (
      <div key={row.userId} className="flex items-center gap-3 px-4 py-3">
        <span className="w-6 shrink-0 text-center text-sm font-semibold ui-muted">{startRank + idx}</span>
        <Avatar avatar={row.avatar || ''} label={row.fullName} size="sm" />
        <div className="min-w-0 flex-1">
          <HonorName row={row} />
        </div>
        <div className="shrink-0 text-right">
          <p className="text-sm font-semibold ui-heading">{formatHonorValue(kind, row.value)}</p>
          {row.detail ? <p className="text-[11px] ui-muted">{row.detail}</p> : null}
        </div>
      </div>
    ))}
  </div>
);

const HonorSkeleton = () => (
  <div className="ui-card animate-pulse divide-y divide-[#E5E7EB] px-4 dark:divide-slate-700">
    {Array.from({ length: 6 }).map((_, idx) => (
      <div key={idx} className="flex items-center gap-3 py-3">
        <div className="h-10 w-10 shrink-0 rounded-full bg-slate-200 dark:bg-slate-700" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 w-1/3 rounded bg-slate-200 dark:bg-slate-700" />
          <div className="h-2.5 w-1/4 rounded bg-slate-200 dark:bg-slate-700" />
        </div>
        <div className="h-3 w-16 shrink-0 rounded bg-slate-200 dark:bg-slate-700" />
      </div>
    ))}
  </div>
);

type LoadStatus = 'loading' | 'success' | 'error';

export const HonorPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { kind, period, tabKey, periodKey } = useMemo(() => parseHonorQuery(searchParams), [searchParams]);

  const [rows, setRows] = useState<HonorRow[]>([]);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [retryTick, setRetryTick] = useState(0);
  // Đếm request để bỏ qua phản hồi đến muộn của lựa chọn tab/kỳ trước đó.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setStatus('loading');

    fetchHonorRows(kind, period)
      .then((result) => {
        if (requestIdRef.current !== requestId) return; // phản hồi muộn - bỏ qua
        setRows(result);
        setStatus('success');
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setRows([]);
        setStatus('error');
      });
  }, [kind, period, retryTick]);

  // Đếm ngược chốt kỳ - cập nhật mỗi 60 giây, ẩn ở "Mọi thời gian".
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (period === 'all') return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, [period]);

  const countdownText = useMemo(() => {
    if (period === 'all') return null;
    const { endsAt } = periodRange(period, new Date());
    if (!endsAt) return null;
    return formatPeriodLeft(endsAt.getTime() - Date.now(), period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, tick]);

  const goTo = (nextTabKey: HonorTabKey, nextPeriodKey: HonorPeriodKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextTabKey);
    next.set('ky', nextPeriodKey);
    setSearchParams(next, { replace: false });
  };

  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3, 20);

  return (
    <div className="mx-auto max-w-3xl px-3 py-4">
      <Helmet>
        <title>Vinh danh · Cab</title>
      </Helmet>

      <div className="mb-4">
        <h1 className="flex items-center gap-2 text-2xl font-bold ui-heading">
          <Trophy className="text-amber-500" size={24} />
          Vinh danh
        </h1>
        <p className="mt-1 text-sm ui-muted">
          Những người nổi bật nhất trên Cab theo từng tuần, từng tháng.
        </p>
      </div>

      <div className="ui-card mb-4">
        <div className="flex overflow-x-auto border-b border-[#E5E7EB] dark:border-slate-700 md:flex-wrap md:overflow-visible">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => goTo(t.key, periodKey)}
              className={`relative shrink-0 whitespace-nowrap px-3 py-3 text-sm md:px-4 font-semibold transition-colors ${
                tabKey === t.key
                  ? 'text-primary'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              {t.label}
              {tabKey === t.key ? (
                <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-primary" />
              ) : null}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => goTo(tabKey, p.key)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  periodKey === p.key
                    ? 'bg-white text-primary shadow-sm dark:bg-slate-700'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {countdownText ? <span className="text-xs ui-muted">{countdownText}</span> : null}
        </div>

        {tabKey === 'donate' ? (
          <p className="px-4 pb-3 text-xs ui-muted">
            Lượt donate ẩn danh không được tính vào bảng này.
          </p>
        ) : null}
      </div>

      {status === 'loading' ? <HonorSkeleton /> : null}

      {status === 'error' ? (
        <div className="ui-card flex flex-col items-center gap-3 py-10 text-center">
          <p className="text-sm ui-muted">Không tải được bảng xếp hạng. Vui lòng thử lại.</p>
          <button
            type="button"
            onClick={() => setRetryTick((t) => t + 1)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white"
          >
            <RefreshCw size={14} /> Thử lại
          </button>
        </div>
      ) : null}

      {status === 'success' && rows.length === 0 ? (
        <div className="ui-card py-10 text-center">
          <p className="text-sm ui-muted">Chưa có ai trong kỳ này.</p>
        </div>
      ) : null}

      {status === 'success' && rows.length > 0 ? (
        <>
          <HonorPodium kind={kind} rows={top3} />
          {rest.length > 0 ? <HonorList kind={kind} rows={rest} startRank={4} /> : null}
        </>
      ) : null}
    </div>
  );
};

export default HonorPage;
